import {stringToColor, truncateStringWithShortcut} from "../utils/utils";
import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import { Star, StarOff, CornerUpRight } from 'lucide-react';
import { cn } from "@/lib/utils";

// Zustand Store
import useCanvasStore from "../stores/canvasStore";

/**
 * ShortcutItem - Displays a shortcut in the canvas
 * 
 * Props (minimal - reads most data from store):
 * - id: shortcut ID
 * - depth: depth level in the canvas
 * - itemIndex: index in the items array for keyboard navigation
 */
const ShortcutItem = ({ id, depth, itemIndex }) => {
    const [href, setHref] = React.useState("");
    
    // User settings from Redux (auth)
    const user_settings = useSelector((state) => state.auth.user_settings);
    const isMinimalView = user_settings?.minimal_mode;
    
    // Read item data and state from Zustand store
    const item = useCanvasStore(s => s.items[depth]?.find(i => i.id === id));
    const selectedItems = useCanvasStore(s => s.selectedItems);
    const focusedIndex = useCanvasStore(s => s.focusedIndex);
    const toggleItemSelection = useCanvasStore(s => s.toggleItemSelection);
    const selectItemRange = useCanvasStore(s => s.selectItemRange);
    const setFocusedIndex = useCanvasStore(s => s.setFocusedIndex);
    const setCurrentDepth = useCanvasStore(s => s.setCurrentDepth);

    // Derived values from store
    const data = item || {};
    const is_archived = data.is_archived || false;
    
    // Normalize employees array (shortcuts use assignee, but we need employees array format)
    const employees = data.assignee ? [data.assignee] : (data.employees || []);
    
    // Check if this item is focused via keyboard navigation
    const isFocused = focusedIndex === `${depth}_${itemIndex}`;
    
    const isSelected = selectedItems.length > 0 && 
                      selectedItems[0].depth === depth &&
                      selectedItems.some(item => item.id === id);

    useEffect(() => {
        if (depth !== -1 && data.original_item) {
            setHref(`${window.location.origin}/folder/${data.original_item}`)
        }
    }, [depth, data.original_item]);

    const handleClick = (e) => {
        e.preventDefault();
        
        // Set focus to this item on any click
        if (itemIndex !== undefined && depth !== undefined) {
            setFocusedIndex(`${depth}_${itemIndex}`);
            setCurrentDepth(depth);
        }
        
        // Multi-select mode: Ctrl+Click (or Cmd+Click on Mac) - toggle single item
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.stopPropagation();
            toggleItemSelection({
                id: data.id,
                parent_id: data.parent,
                depth: depth,
                item_type: 'shortcut',
                original_item: data.original_item,
                name: data.name,
                is_archived: is_archived,
                employees: employees
            }, depth, itemIndex);
            return;
        }
        
        // Range select: Shift+Click (with or without Ctrl) - select all items from anchor to clicked
        if (e.shiftKey) {
            e.stopPropagation();
            selectItemRange({
                id: data.id,
                parent_id: data.parent,
                item_type: 'shortcut',
                original_item: data.original_item,
                name: data.name,
                is_archived: is_archived,
                employees: employees
            }, depth, itemIndex);
            return;
        }
    };
    
    const handleDoubleClick = (e) => {
        // Only navigate on double-click if not holding Ctrl/Cmd
        if (!e.ctrlKey && !e.metaKey && href) {
            window.location.href = href;
        }
    };

    // Don't render if item doesn't exist in store
    if (!item) return null;

    return (
        <div data-name="shortcut-item-container" className="flex w-full">
            <div 
                data-name="shortcut-item-main"
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                className={cn(
                    "folder-container w-full flex",
                    isSelected && "selected",
                    isFocused && "ring-2 ring-primary ring-offset-1"
                )}
            >
            {/* Left content */}
            <div className="flex-1 min-w-0 flex flex-col items-start">
                {/* Header row */}
                <div className="flex items-center gap-1 w-full overflow-hidden">
                    <span className="flex-shrink-0">
                        {data.star_level > 0 ? 
                            <Star className={cn(
                                "w-4 h-4 fill-current",
                                is_archived ? "text-muted-foreground" :
                                data.star_level === 3 ? "text-destructive" : 
                                data.star_level === 2 ? "text-warning" : "text-success"
                            )}/> : 
                            <StarOff className={cn(
                                "w-4 h-4",
                                is_archived ? "text-muted-foreground" : "text-muted-foreground"
                            )}/>
                        }
                    </span>
                    <CornerUpRight className={cn(
                        "w-4 h-4 flex-shrink-0",
                        is_archived ? "text-muted-foreground" : "text-foreground"
                    )} />
                    <div className="min-w-0 flex-1">
                        <span className={cn(
                            "text-sm truncate block cursor-pointer select-none",
                            is_archived ? "text-muted-foreground" : "text-foreground"
                        )}>
                            {truncateStringWithShortcut(data.name, 45)}
                        </span>
                    </div>
                </div>
                
                {/* Tags row */}
                {!isMinimalView && data.tags && Array.isArray(data.tags) && data.tags.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {data.tags.slice(0, 3).map((tag, index) => (
                            <span 
                                key={index}
                                className="inline-flex items-center px-1.5 py-0 text-[9px] rounded border border-border bg-secondary text-secondary-foreground"
                            >
                                {tag.name}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Right side - assignee avatar */}
            <div className="flex items-center justify-center ml-1 flex-shrink-0 w-10">
                {!isMinimalView && employees && Array.isArray(employees) && employees.slice(0, 1).map((employee, index) => (
                    <span
                        key={index}
                        className="w-6 h-6 rounded flex items-center justify-center text-[9px] font-semibold text-white"
                        style={{backgroundColor: stringToColor(employee.first_name + employee.last_name)}}
                    >
                        {employee.first_name.charAt(0)}{employee.first_name.charAt(1) || ''}{employee.last_name.charAt(0)}{employees.length > 1 ? '..' : ''}
                    </span>
                    ))}
            </div>
        </div>
    </div>
    )
}

export default ShortcutItem;
